/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";

import { IMPORT_SECTION_ASSET } from "../graphql";

const log: any = LoggerModule?.getInstance();

export interface ImportSectionAssetProps {
  costumeFeatureId: string;
  costumeTypeId: string;
}

export interface ImportSectionAssetsProps {
  costumeFeatureIds: string[];
  costumeTypeId: string;
}

export interface IImportSectionAssetMethods extends IPluginMethodMap {
  importSectionAsset(args: ImportSectionAssetProps): Promise<any>;
  importSectionAssets(args: ImportSectionAssetsProps): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getDisplayMessage = (err: any) => {
  const displayMessage =
    err?.graphQLErrors &&
    Array.isArray(err?.graphQLErrors) &&
    err?.graphQLErrors.find((item: any) => item.extensions);

  return (
    displayMessage?.extensions?.display_message ||
    err?.message ||
    "Something went wrong"
  );
};

export class ImportSectionAssetService implements IAgentPlugin {
  readonly methods: IImportSectionAssetMethods;

  constructor() {
    this.methods = {
      importSectionAsset: this.importSectionAsset.bind(this),
      importSectionAssets: this.importSectionAssets.bind(this),
    };
  }

  async importSectionAsset(args: ImportSectionAssetProps) {
    log?.blue?.("ImportSectionAssetService.importSectionAsset:", args);

    try {
      const graphqlModule = getGraphQLModule();
      const client = graphqlModule.getClient("graphql-gateway");

      if (!client) {
        throw new Error("GraphQL client not found");
      }

      if (!args?.costumeFeatureId) {
        throw new Error("Missing costume feature id");
      }

      if (!args?.costumeTypeId) {
        throw new Error("Missing costume type id");
      }

      const res = await client.mutate({
        mutation: IMPORT_SECTION_ASSET,
        variables: {
          costumeFeatureId: args.costumeFeatureId,
          costumeTypeId: args.costumeTypeId,
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.importSectionAsset;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }

  async importSectionAssets(args: ImportSectionAssetsProps) {
    log?.blue?.("ImportSectionAssetService.importSectionAssets:", args);

    if (!Array.isArray(args?.costumeFeatureIds)) {
      throw new Error("Missing costume feature ids");
    }

    if (!args?.costumeTypeId) {
      throw new Error("Missing costume type id");
    }

    const importedAssets: any[] = [];

    for (const costumeFeatureId of args.costumeFeatureIds) {
      if (!costumeFeatureId) continue;

      const imported = await this.importSectionAsset({
        costumeFeatureId,
        costumeTypeId: args.costumeTypeId,
      });

      if (imported) {
        importedAssets.push(imported);
      }
    }

    return {
      importedAssets,
      count: importedAssets.length,
    };
  }
}
